import { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarBlank, MapPin, Clock, Users, Wine, Heart, Sun, Camera } from '@phosphor-icons/react';

const API = '/api';

// Default schedule fallback
const DEFAULT_SCHEDULE = [
  {
    date: 'Thursday, December 10',
    date_es: 'Jueves, 10 de Diciembre',
    events: [
      {
        time: '6:00 PM',
        title: 'Welcome Party',
        title_es: 'Fiesta de Bienvenida',
        location: '',
        description: 'More information to come soon.',
        description_es: 'Más información próximamente.',
        dress: 'Casual',
      },
    ],
  },
  {
    date: 'Friday, December 11',
    date_es: 'Viernes, 11 de Diciembre',
    is_wedding_day: true,
    events: [
      {
        time: '3:30 PM',
        title: 'Wedding Ceremony',
        title_es: 'Ceremonia de Boda',
        location: 'Cardedeu - Chapel',
        description: 'Witness our exchange of vows in a beautiful ceremony overlooking Lake Coatapeque.',
        description_es: 'Sé testigo de nuestro intercambio de votos en una hermosa ceremonia con vista al Lago de Coatepeque.',
        dress: 'Black Tie Optional',
      },
      {
        time: '4:00 PM',
        title: 'Cocktail Hour',
        title_es: 'Hora del Cóctel',
        location: 'Cardedeu',
        description: 'Enjoy cocktails, hors d\'oeuvres, and live music while we take photos.',
        description_es: 'Disfruta de cócteles, aperitivos y música en vivo mientras tomamos fotos.',
        dress: 'Black Tie Optional',
      },
      {
        time: '7:00 PM',
        title: 'Reception',
        title_es: 'Recepción',
        location: 'Cardedeu',
        description: 'Join us for a seated dinner, heartfelt toasts, and celebration.',
        description_es: 'Acompáñanos para una cena formal, brindis emotivos y celebración.',
        dress: 'Black Tie Optional',
      },
    ],
  },
  {
    date: 'Saturday, December 12',
    date_es: 'Sábado, 12 de Diciembre',
    events: [
      {
        time: '10:00 AM',
        title: 'The Morning After',
        title_es: 'La Mañana Después',
        location: '',
        description: 'Join us lakeside for a relaxed day on the water.',
        description_es: 'Acompáñanos junto al lago para un día relajado en el agua.',
        dress: 'Casual',
      },
    ],
  },
];

// Helper to get icon based on event title
const getEventIcon = (title) => {
  const titleLower = (title || '').toLowerCase();
  if (titleLower.includes('ceremony') || titleLower.includes('wedding')) return <Heart size={20} weight="light" />;
  if (titleLower.includes('cocktail') || titleLower.includes('welcome') || titleLower.includes('party')) return <Wine size={20} weight="light" />;
  if (titleLower.includes('reception') || titleLower.includes('dinner')) return <Users size={20} weight="light" />;
  if (titleLower.includes('morning') || titleLower.includes('brunch')) return <Sun size={20} weight="light" />;
  return <CalendarBlank size={20} weight="light" />;
};

// Default dress codes
const DEFAULT_DRESS_CODES = {
  casual: {
    title: 'Casual',
    title_es: 'Informal',
    description: 'Comfortable wear. Sundresses, linen shirts, shorts, sandals. Don\'t forget swimwear!',
    description_es: 'Ropa cómoda. Vestidos de verano, camisas de lino, shorts, sandalias. ¡No olvides el traje de baño!'
  },
  black_tie_optional: {
    title: 'Black Tie Optional',
    title_es: 'Etiqueta Opcional',
    description: 'Elegant formal attire. Floor-length gowns. Tuxedos or Suits. No white or ivory.',
    description_es: 'Atuendo formal elegante. Vestidos largos. Esmoquin o trajes. No blanco ni marfil.'
  }
};

export default function ItineraryPage() {
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [dressCodes, setDressCodes] = useState(DEFAULT_DRESS_CODES);

  useEffect(() => {
    const fetchItinerary = async () => {
      try {
        const response = await axios.get(`${API}/content/itinerary`);
        if (response.data) {
          if (response.data.days && response.data.days.length > 0) {
            setSchedule(response.data.days);
          }
          if (response.data.dress_codes) {
            setDressCodes(prev => ({ ...prev, ...response.data.dress_codes }));
          }
        }
      } catch (error) {
        console.log('Using default schedule');
      }
    };
    fetchItinerary();
  }, []);

  return (
    <div data-testid="itinerary-page" className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="page-header bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-1" style={{ color: "#B8A88A" }}>
            Wedding Weekend
          </p>
          <p className="font-body text-xs italic text-stone-400 mb-4">
            Fin de Semana de Boda
          </p>
          <h1 className="page-title">Itinerary</h1>
          <p className="font-body text-sm italic text-stone-400 mb-2">Itinerario</p>
          <p className="page-subtitle">
            December 10-12, 2026 • Lake Coatapeque, El Salvador
          </p>
          <p className="font-body text-xs italic text-stone-400">
            10-12 de Diciembre, 2026 • Lago de Coatepeque, El Salvador
          </p>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4">
          {schedule.map((day, dayIndex) => (
            <div key={dayIndex} className="mb-16 last:mb-0">
              {/* Day Header */}
              <div className={`mb-8 p-6 ${day.is_wedding_day ? 'bg-[#C9B99A] text-white' : 'bg-white border border-stone-200'}`}>
                <div className="flex items-center gap-3">
                  <CalendarBlank size={24} weight="light" className={day.is_wedding_day ? 'text-white/80' : 'text-[#B8A88A]'} />
                  <div>
                    <h2 className={`font-display text-2xl ${day.is_wedding_day ? 'text-white' : 'text-stone-900'}`}>
                      {day.date}
                    </h2>
                    <p className={`font-body text-sm italic ${day.is_wedding_day ? 'text-white/70' : 'text-stone-400'}`}>
                      {day.date_es}
                    </p>
                    {day.is_wedding_day && (
                      <p className="font-body text-sm tracking-wide text-white/80 mt-1">
                        The Wedding Day / El Día de la Boda
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Events */}
              <div className="space-y-0">
                {(day.events || []).map((event, eventIndex) => (
                  <div
                    key={eventIndex}
                    data-testid={`event-${dayIndex}-${eventIndex}`}
                    className="timeline-item"
                  >
                    <div className="timeline-dot" />
                    
                    <div className="bg-white border border-stone-200 p-6 ml-4">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center text-[#B8A88A]">
                          {getEventIcon(event.title)}
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <span className="timeline-time flex items-center gap-1">
                              <Clock size={14} />
                              {event.time}
                            </span>
                          </div>
                          <h3 className="timeline-title">{event.title}</h3>
                          <p className="font-body text-sm italic text-stone-400">{event.title_es}</p>
                          {event.location && (
                            <div className="flex items-center gap-1 text-stone-500 text-sm mt-1 mb-3">
                              <MapPin size={14} />
                              {event.location}
                            </div>
                          )}
                          <p className="timeline-description mt-3">
                            {event.description}
                          </p>
                          <p className="font-body text-sm italic text-stone-400 mt-2">
                            {event.description_es}
                          </p>
                          <div className="mt-4 pt-4 border-t border-stone-100">
                            <span className="font-body text-xs uppercase tracking-wider text-stone-400">
                              Dress Code / Código de Vestimenta:
                            </span>
                            <span className="font-body text-sm text-stone-700 ml-2">
                              {event.dress}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dress Code Guide */}
      <section data-testid="dress-code-section" className="py-16 bg-white border-t border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-2xl md:text-3xl text-stone-900 mb-1">
              Dress Code Guide
            </h2>
            <p className="font-body text-sm italic text-stone-400">
              Guía de Código de Vestimenta
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Casual - Now with champagne background (swapped) */}
            <div className="p-6 bg-[#C9B99A] text-center">
              <h3 className="font-display text-lg text-white mb-1">
                {dressCodes.casual?.title || 'Casual'}
              </h3>
              <p className="font-body text-xs italic text-white/70 mb-3">
                {dressCodes.casual?.title_es || 'Informal'}
              </p>
              <p className="font-body text-sm text-stone-200">
                {dressCodes.casual?.description || 'Comfortable wear. Sundresses, linen shirts, shorts, sandals. Don\'t forget swimwear!'}
              </p>
              <p className="font-body text-xs italic text-white/70 mt-2">
                {dressCodes.casual?.description_es || 'Ropa cómoda. Vestidos de verano, camisas de lino, shorts, sandalias. ¡No olvides el traje de baño!'}
              </p>
            </div>
            {/* Black Tie Optional - Now with light background (swapped) */}
            <div className="p-6 bg-stone-50 border border-stone-200 text-center">
              <h3 className="font-display text-lg text-stone-900 mb-1">
                {dressCodes.black_tie_optional?.title || 'Black Tie Optional'}
              </h3>
              <p className="font-body text-xs italic text-stone-400 mb-3">
                {dressCodes.black_tie_optional?.title_es || 'Etiqueta Opcional'}
              </p>
              <p className="font-body text-sm text-stone-600">
                {dressCodes.black_tie_optional?.description || 'Elegant formal attire. Floor-length gowns. Tuxedos or Suits. No white or ivory.'}
              </p>
              <p className="font-body text-xs italic text-stone-400 mt-2">
                {dressCodes.black_tie_optional?.description_es || 'Atuendo formal elegante. Vestidos largos. Esmoquin o trajes. No blanco ni marfil.'}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
